import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import { Link, useNavigate } from "react-router-dom";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import Select from "react-select";
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from "../staff/dropdownStyles";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import useThemeColor from "../../hooks/useThemeColor";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import ThemeButton from "../../components/CustomComponents/Buttons/ThemeButton/ThemeButton";
import swal from "sweetalert";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";


import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import { getModernGridFilterPillSelectStyles } from "../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import CustomModal from "../../components/CustomComponents/modal/customModal";

const DEAL_SORT_FIELDS = {
  name: "DealName",
  price: "DealPrice",
  status: "IsActive",
  startDate: "StartDate",
  endDate: "EndDate",
  sortOrder: "DealOrder",
};

const DEAL_SORT_LABELS = {
  [DEAL_SORT_FIELDS.name]: "Deal Name",
  [DEAL_SORT_FIELDS.price]: "Price",
  [DEAL_SORT_FIELDS.status]: "Status",
  [DEAL_SORT_FIELDS.startDate]: "Start Date",
  [DEAL_SORT_FIELDS.endDate]: "End Date",
  [DEAL_SORT_FIELDS.sortOrder]: "Sort Order",
};

const ManageDeals = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );
  const themeColor = useThemeColor();

  const SHOW_BULK_ACTIONS = false;

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedDeals, setSelectedDeals] = useState(new Set());
  const [compCurrency, setCompCurrency] = useState("GBP");
  const lastSelectedIndex = useRef(null);
  const dealsFetchSeqRef = useRef(0);
  const [statusConfirmDeal, setStatusConfirmDeal] = useState(null);

  const pageKey = "deals";
  const defaultVisibleColumns = {
    name: true,
    price: true,
    status: true,
    startDate: false,
    endDate: false,
  };

  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "DealOrder", label: "Order By" };
  const defaultStatus = { value: "", label: "All Status" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const status = preferences.status || defaultStatus;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const statusSelectValue =
    statusOptions.find((o) => String(o.value) === String(status?.value ?? "")) ??
    defaultStatus;

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) =>
    currencySymbols[currencyCode] || currencyCode;

  const navigate = useNavigate();

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const fetchCompCurrency = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_PROFILE);
      const profileData = response.data.data;
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      showErrorToast("Failed to load company currency");
    }
  }, []);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      const mergedColumns = mergeVisibleColumns(
        preferences.visibleColumns,
        defaultVisibleColumns
      );
      dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (!preferences.status) {
      dispatch(setFilterField({ pageKey, field: "status", value: defaultStatus }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }

    fetchCompCurrency();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey, fetchCompCurrency]);

  const fetchDealsData = useCallback(async () => {
    const fetchSeq = ++dealsFetchSeqRef.current;

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_DEALS_FOR_GRID, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
          PageNumber: pageNumber,
          PageSize: pageSize.value,
          Status: status?.value !== "" ? String(status?.value) : "",
        },
      });

      if (fetchSeq !== dealsFetchSeqRef.current) return;

      const dealsData = response.data.data || response.data;
      const records = response.data.totalRecords || 0;

      setTotalPages(response.data.totalPages || 0);
      setTotalRecords(records);
      setDeals(Array.isArray(dealsData) ? dealsData : []);

      const noSearch = !search.trim();
      const noStatus = status?.value === "" || status?.value === undefined;
      if (noSearch && noStatus) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, records);
      }
    } catch (err) {
      if (fetchSeq !== dealsFetchSeqRef.current) return;
      setDeals([]);
      setTotalPages(0);
      setTotalRecords(0);
      showErrorToast("Failed to load deals");
    } finally {
      if (fetchSeq === dealsFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [search, sortBy, sortOrder, CompID, pageNumber, pageSize, status]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    status?.value,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(dealsFetchSeqRef, [
    search,
    status?.value,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchDealsData(), [fetchDealsData]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    status?.value,
    pageNumber,
    pageSize?.value,
    sortBy?.value,
    sortOrder?.value,
  ]);

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: DEAL_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const handleAddDealClick = () => {
    navigate("/deal/createDeal");
  };

  const handleDeleteDeal = async (deal) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${deal.dealName}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_DEAL, {
        params: {
          DealID: deal.dealID,
          CompID,
        },
      });

      if (response.data.status) {
        showSuccessToast("Deal is Deleted.");
        fetchDealsData();
      } else {
        showErrorToast(response.data.message || "Deletion failed");
        setLoading(false);
      }
    } catch (err) {
      showErrorToast("Something Went Wrong.");
      setLoading(false);
    }
  };

  const handleSelectDeal = (dealId, index, event) => {
    setSelectedDeals((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);

        for (let i = start; i <= end; i++) {
          if (deals[i]?.dealID) {
            newSelected.add(deals[i].dealID);
          }
        }
      } else if (newSelected.has(dealId)) {
        newSelected.delete(dealId);
      } else {
        newSelected.add(dealId);
      }

      lastSelectedIndex.current = index;
      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedDeals(new Set(deals.map((deal) => deal.dealID)));
      lastSelectedIndex.current = null;
    } else {
      setSelectedDeals(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedDeals(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedDeals.size === 0) {
      showErrorToast("No deals selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedDeals.size} selected deals?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedDeals).map((dealId) =>
          axiosInstance.delete(APIs.DELETE_DEAL, {
            params: {
              DealID: dealId,
              CompID,
            },
          })
        )
      );

      const deletedCount = selectedDeals.size;
      setSelectedDeals(new Set());
      lastSelectedIndex.current = null;
      fetchDealsData();
      showSuccessToast(`${deletedCount} deals deleted successfully.`);
    } catch (err) {
      showErrorToast("Failed to delete selected deals.");
      setLoading(false);
    }
  };

  const confirmDealStatusChange = async () => {
    if (!statusConfirmDeal) return;

    const { dealID, isActive, dealName } = statusConfirmDeal;
    const newStatus = !isActive;
    const deal = deals.find((d) => d.dealID === dealID);

    setDeals((prev) =>
      prev.map((d) => (d.dealID === dealID ? { ...d, isActive: newStatus } : d))
    );
    setStatusConfirmDeal(null);

    try {
      await axiosInstance.post(APIs.POST_ADD_OR_UPDATE_DEAL, {
        ...deal,
        isActive: newStatus,
      });
      showSuccessToast(
        `"${dealName || "Deal"}" is now ${newStatus ? "Active" : "InActive"}.`
      );
    } catch (error) {
      setDeals((prev) =>
        prev.map((d) => (d.dealID === dealID ? { ...d, isActive } : d))
      );
      showErrorToast("Failed to update deal status.");
    }
  };

  const clearFilters = () => {
    setSearch("");
    dispatch(setFilterField({ pageKey, field: "status", value: defaultStatus }));
  };

  const hasFiltersApplied = () =>
    search.trim() !== "" || (status && status.value !== "");

  const isUnfilteredView = !hasFiltersApplied();

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    isUnfilteredView &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    const parts = [];
    if (search.trim()) {
      parts.push(`search "${search.trim()}"`);
    }
    if (status?.value !== "" && status?.value !== undefined) {
      parts.push(`status "${statusSelectValue?.label ?? status.label}"`);
    }
    if (parts.length === 0) {
      return "There are no deals to show right now. Add a new deal or check back later.";
    }
    return `No deals match your current ${parts.join(", ")}. Try different filters or clear them to see all deals.`;
  };

  const renderNoDataMessage = () => {
    if (loading || deals.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No deals found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  const renderBulkSelectionActions = () => {
    if (!SHOW_BULK_ACTIONS || selectedDeals.size === 0) return null;

    return (
      <div className="mb-3">
        <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between">
          <span className="fw-bold text-primary">
            {selectedDeals.size} deal{selectedDeals.size > 1 ? "s" : ""} selected
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={handleDeselectAll}
          >
            Clear Selection
          </button>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={handleBulkDelete}
          >
            <i className="fa fa-trash me-1" />
            Delete
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Deals</li>
          </ol>
          <h1 className="page-header mb-0">Manage Deals</h1>
        </div>
        <div className="ms-auto">
          <ThemeButton icon="fas fa-plus" onClick={handleAddDealClick}>
            Add Deal
          </ThemeButton>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && (
          <AddButton onClick={handleAddDealClick} title="Add Deal" />
        )}

        {showGridToolbar && (
          <>
            <ModernGridToolbar
              isDarkMode={isDarkMode}
              title="Deals"
              totalCount={totalRecords}
              searchValue={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search by name, description..."
              bodyFlush
              topFilters={
                <>
                  <ModernGridToolbarFilterPill
                    iconClass="fa fa-filter"
                    className="modern-grid-toolbar__filter-pill--status"
                  >
                    <Select
                      value={statusSelectValue}
                      onChange={(val) =>
                        dispatch(
                          setFilterField({
                            pageKey,
                            field: "status",
                            value: val,
                          })
                        )
                      }
                      options={statusOptions}
                      styles={filterPillSelectStyles}
                      placeholder="All Status"
                      isSearchable={false}
                      {...defaultSelectPortalProps}
                    />
                  </ModernGridToolbarFilterPill>
                  {renderBulkSelectionActions()}
                </>
              }
              headerControls={
                <ColumnSelection
                  visibleColumns={visibleColumns}
                  setVisibleColumns={(cols) =>
                    dispatch(setVisibleColumns({ pageKey, columns: cols }))
                  }
                />
              }
            >
              <div className="px-3 pb-3">
                <GridSectionLoader loading={loading} minHeight="280px">
                  {renderNoDataMessage()}

                  {Array.isArray(deals) && deals.length > 0 && (
                    <div
                      className="table-responsive table-container"
                      style={{
                        minHeight: "200px",
                        maxHeight: "450px",
                        overflowY: "auto",
                        overflowX: "auto",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      <table
                        className="table table-hover"
                        style={{
                          position: "relative",
                          "--modern-grid-sort-active": themeColor,
                        }}
                      >
                        <thead
                          className="bg-light sticky-header"
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 10000,
                            backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                            color: isDarkMode ? "#ffffff" : "#000000",
                          }}
                        >
                          <tr>
                            {SHOW_BULK_ACTIONS && (
                              <th className="pt-2 pb-2">
                                <input
                                  type="checkbox"
                                  onChange={handleSelectAll}
                                  checked={
                                    deals.length > 0 &&
                                    selectedDeals.size === deals.length
                                  }
                                />
                              </th>
                            )}
                            {visibleColumns.name && (
                              <SortableColumnHeader
                                sortField={DEAL_SORT_FIELDS.name}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                              >
                                Deal Name
                              </SortableColumnHeader>
                            )}
                            {visibleColumns.price && (
                              <SortableColumnHeader
                                sortField={DEAL_SORT_FIELDS.price}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                                className="text-center"
                              >
                                Price ({getCurrencySymbol(compCurrency)})
                              </SortableColumnHeader>
                            )}
                            {visibleColumns.startDate && (
                              <SortableColumnHeader
                                sortField={DEAL_SORT_FIELDS.startDate}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                                className="text-center"
                              >
                                Start Date
                              </SortableColumnHeader>
                            )}
                            {visibleColumns.endDate && (
                              <SortableColumnHeader
                                sortField={DEAL_SORT_FIELDS.endDate}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                                className="text-center"
                              >
                                End Date
                              </SortableColumnHeader>
                            )}
                            {visibleColumns.status && (
                              <SortableColumnHeader
                                sortField={DEAL_SORT_FIELDS.status}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                                className="text-center"
                              >
                                Status
                              </SortableColumnHeader>
                            )}
                            <th className="pt-2 pb-2 text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deals.map((deal, index) => (
                            <tr key={deal.dealID}>
                              {SHOW_BULK_ACTIONS && (
                                <td className="align-middle">
                                  <input
                                    type="checkbox"
                                    checked={selectedDeals.has(deal.dealID)}
                                    onChange={(e) =>
                                      handleSelectDeal(deal.dealID, index, e)
                                    }
                                    data-chkbox-shiftsel="type1"
                                  />
                                </td>
                              )}
                              {visibleColumns.name && (
                                <td
                                  className="align-middle"
                                  onClick={() =>
                                    navigate(`/deal/updateDeal/${deal.dealID}`)
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  {deal.dealName}
                                </td>
                              )}
                              {visibleColumns.price && (
                                <td
                                  className="align-middle text-center"
                                  onClick={() =>
                                    navigate(`/deal/updateDeal/${deal.dealID}`)
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  {getCurrencySymbol(compCurrency)}{" "}
                                  {deal.dealPrice?.toFixed(2) || "0.00"}
                                </td>
                              )}
                              {visibleColumns.startDate && (
                                <td className="align-middle text-center">
                                  {deal.startDate
                                    ? new Date(deal.startDate).toLocaleDateString()
                                    : "N/A"}
                                </td>
                              )}
                              {visibleColumns.endDate && (
                                <td className="align-middle text-center">
                                  {deal.endDate
                                    ? new Date(deal.endDate).toLocaleDateString()
                                    : "N/A"}
                                </td>
                              )}
                              {visibleColumns.status && (
                                <td className="align-middle text-center">
                                  <StatusBadge
                                    isActive={deal.isActive}
                                    onClick={() => setStatusConfirmDeal(deal)}
                                  />
                                </td>
                              )}
                              <td className="align-middle text-center">
                                <TableActionsMenu>
                                  <TableActionsItem
                                    as={Link}
                                    to={`/deal/updateDeal/${deal.dealID}`}
                                    icon="fa fa-edit"
                                  >
                                    Edit
                                  </TableActionsItem>
                                  <TableActionsItem
                                    danger
                                    icon="fa fa-trash"
                                    onClick={() => handleDeleteDeal(deal)}
                                  >
                                    Delete
                                  </TableActionsItem>
                                </TableActionsMenu>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <ModernGridPagerFooter
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    totalRecords={totalRecords}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    setPageNumber={setPageNumber}
                  />
                </GridSectionLoader>
              </div>
            </ModernGridToolbar>
          </>
        )}
      </div>

      <CustomModal
        title="Change deal status?"
        subtitle={
          statusConfirmDeal
            ? `Are you sure you want to set "${statusConfirmDeal.dealName || "this deal"}" to ${
                statusConfirmDeal.isActive ? "InActive" : "Active"
              }?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={() => setStatusConfirmDeal(null)}
        isOpen={!!statusConfirmDeal}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmDealStatusChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />
    </div>
  );
};

export default ManageDeals;
